import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth, useClerk } from "@clerk/react";
import { api, setPracticeId, setTokenGetter } from "@/lib/api";
import { setDateFormat } from "@/lib/status";
import TopBar from "@/components/TopBar";
import LockScreen from "@/components/LockScreen";
import Workspace from "@/pages/Workspace";
import TemplatesPage from "@/pages/TemplatesPage";
import ImportsPage from "@/pages/ImportsPage";
import ReportsPage from "@/pages/ReportsPage";
import AdminPage from "@/pages/AdminPage";
import SchedulePage from "@/pages/SchedulePage";
import VerifyPage from "@/pages/VerifyPage";
import EnterprisePage from "@/pages/EnterprisePage";
import ChangePasswordScreen from "@/components/ChangePasswordScreen";
import { Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { UserMenu } from "@/components/UserMenu";

export const PracticeCtx = createContext(null);
export const usePractice = () => useContext(PracticeCtx);

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="app-loading">
    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading workspace...
  </div>
);

function AccessPending({ me }) {
  const { signOut, openUserProfile } = useClerk();
  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="access-pending">
      <div className="max-w-md bg-white border rounded-xl p-8">
        <ShieldAlert className="w-8 h-8 text-amber-600" />
        <h1 className="text-2xl font-bold mt-4">Access pending</h1>
        <p className="mt-2 text-sm text-slate-600">You are signed in as <b>{me.email}</b> but have no approved organization membership. An organization administrator must add you before any patient records are visible.</p>
        {me.suspended_orgs?.length > 0 && <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="access-suspended-note">Suspended: {me.suspended_orgs.join(", ")}. Contact a Super Admin.</p>}
        <div className="mt-6 flex gap-2">
          <Button variant="outline" onClick={() => openUserProfile()} data-testid="access-pending-account">Manage account / password</Button>
          <Button variant="outline" onClick={() => signOut()} data-testid="access-pending-signout">Sign out</Button>
        </div>
      </div>
    </div>
  );
}

function Guard({ perm, children }) {
  const { can } = usePractice();
  const loc = useLocation();
  if (can(perm)) return children;
  if (loc.pathname !== "/") return <Navigate to="/" replace />;
  return (
    <div className="h-full flex items-center justify-center p-6" data-testid="access-denied">
      <div className="max-w-md bg-white border rounded-xl p-8 text-center"><ShieldAlert className="w-8 h-8 text-amber-600 mx-auto" />
        <h1 className="text-xl font-bold mt-3">No access to this screen</h1>
        <p className="text-sm text-slate-600 mt-2">Your role does not include this screen. Ask an organization administrator to change it in the Authorization Manager.</p></div>
    </div>
  );
}

function useIdle(onIdle, ms, enabled) {
  const t = useRef();
  useEffect(() => {
    if (!enabled) return undefined;
    const reset = () => { clearTimeout(t.current); t.current = setTimeout(onIdle, ms); };
    const evs = ["pointerdown", "keydown", "scroll"];
    evs.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => { clearTimeout(t.current); evs.forEach((e) => window.removeEventListener(e, reset)); };
  }, [onIdle, ms, enabled]);
}

function EnterpriseOnly({ me, reloadMe }) {
  return (
    <PracticeCtx.Provider value={{ me, reloadMe }}>
      <div className="h-screen flex flex-col" data-testid="enterprise-only">
        <header className="h-16 shrink-0 border-b bg-white flex items-center gap-3 px-4">
          <img src="/ace-mark.png" alt="ACE eSIGN" className="h-9 w-auto" />
          <span className="font-semibold">Enterprise</span>
          <span className="text-xs text-slate-500 hidden sm:inline">You are not a member of any organization yet</span>
          <span className="ml-auto"><UserMenu me={me} roleLabel="Super Admin" showVersion /></span>
        </header>
        <div className="flex-1 min-h-0"><EnterprisePage onChanged={reloadMe} /></div>
      </div>
    </PracticeCtx.Provider>
  );
}

export default function StaffApp() {
  const { getToken } = useAuth();
  const [me, setMe] = useState(null);
  const [pid, setPid] = useState(sessionStorage.getItem("ace.practice"));
  const [ctx, setCtx] = useState(null);
  const [locked, setLocked] = useState(false);
  const [facility, setFacility] = useState("");

  setTokenGetter(() => getToken());
  const loadMe = useCallback(async () => {
    const { data } = await api.get("/me");
    setMe(data);
    setLocked(data.locked);
  }, []);
  useEffect(() => { loadMe().catch(() => {}); }, [loadMe]);
  useEffect(() => {
    const f = () => { setLocked(true); setCtx(null); };
    window.addEventListener("ace:locked", f);
    return () => window.removeEventListener("ace:locked", f);
  }, []);
  useEffect(() => {
    const f = () => { sessionStorage.removeItem("ace.practice"); setCtx(null); setPid(null); loadMe().catch(() => {}); };
    window.addEventListener("ace:org-suspended", f);
    return () => window.removeEventListener("ace:org-suspended", f);
  }, [loadMe]);

  const active = me?.memberships.find((m) => m.tenant_id === pid) || me?.memberships.find((m) => m.tenant_id === me.default_org_id) || me?.memberships[0];
  useEffect(() => {
    if (!active || locked || me?.must_change_password) return;
    setPracticeId(active.tenant_id);
    sessionStorage.setItem("ace.practice", active.tenant_id);
    api.get("/practice").then((r) => setCtx(r.data)).catch(() => {});
  }, [active?.tenant_id, locked, me?.must_change_password]); // eslint-disable-line react-hooks/exhaustive-deps

  const idleLock = useCallback(() => {
    api.post("/me/lock").then(() => { setLocked(true); setCtx(null); }).catch(() => {});
  }, []);
  useIdle(idleLock, 15 * 60 * 1000, !!me?.has_pin && !locked);

  if (!me) return <Loading />;
  if (me.must_change_password) return <ChangePasswordScreen onDone={loadMe} />;
  if (locked) return <LockScreen onUnlocked={() => { setLocked(false); loadMe(); }} me={me} />;
  if (!me.memberships.length) return me.super_admin ? <EnterpriseOnly me={me} reloadMe={loadMe} /> : <AccessPending me={me} />;
  if (!ctx) return <Loading />;
  const p = ctx.practice;
  setDateFormat(p.date_format);
  const value = {
    ...ctx, me, reloadMe: loadMe, lockNow: idleLock,
    reloadPractice: () => api.get("/practice").then((r) => setCtx(r.data)).catch(() => {}),
    can: (perm) => ctx.perms.includes(perm),
    canAny: (...ps) => ps.some((x) => ctx.perms.includes(x)),
    switchPractice: (id) => { setCtx(null); setFacility(""); setPid(id); },
    facility, setFacility,
  };
  return (
    <PracticeCtx.Provider value={value}>
      <div key={p.id} className="h-screen flex flex-col ace-watermark" style={{ "--brand": p.color, "--brand-soft": p.soft }} data-testid="staff-app">
        <TopBar />
        <div className="flex-1 min-h-0">
          <Routes>
            <Route path="/" element={<Guard perm="packet:read"><Workspace /></Guard>} />
            <Route path="/schedule" element={<Guard perm="schedule:read"><SchedulePage /></Guard>} />
            <Route path="/templates" element={<Guard perm="template:read"><TemplatesPage /></Guard>} />
            <Route path="/imports" element={<Guard perm="import:run"><ImportsPage /></Guard>} />
            <Route path="/reports" element={<Guard perm="report:read"><ReportsPage /></Guard>} />
            <Route path="/admin" element={<AdminPage />} />
            {me.super_admin ? <Route path="/enterprise" element={<EnterprisePage onChanged={loadMe} />} /> : <Route path="/enterprise" element={<Guard perm="super_admin"><div /></Guard>} />}
            <Route path="/verify/:pid" element={<VerifyPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </PracticeCtx.Provider>
  );
}
